import React from "react";
import { parseErrorMessage } from "@/lib/parseErrorMessage";
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAdAccount } from '@/contexts/AdAccountContext';
import { supabase } from '@/integrations/supabase/client';
import { useSignedThumbnailUrls, resolveThumbnailUrl } from '@/hooks/useSignedThumbnailUrls';
import CreativeFilters from '@/components/creative-library/CreativeFilters';
import CreativeCard from '@/components/creative-library/CreativeCard';
import { BulkTagEditor } from '@/components/creative-library/BulkTagEditor';
import { CreativeTagEditor } from '@/components/creative-library/CreativeTagEditor';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ImageIcon, ImageOff, SearchX, Upload, Layers, X, CheckSquare, Trash2, Tag } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { Creative, calculateAspectRatioLabel } from '@/types/creative';

const CreativeLibrary = () => {
  const { user } = useAuth();
  const { selectedAdAccountId, selectedAdAccountName } = useAdAccount();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Data state
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state — account is now driven by context
  const [selectedType, setSelectedType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRatio, setSelectedRatio] = useState<string>('all');
  const [groupTypeFilter, setGroupTypeFilter] = useState<string>('all');

  // Selection state for bulk operations
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAutoPairing, setIsAutoPairing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkTagEditorOpen, setBulkTagEditorOpen] = useState(false);
  const [detailCreative, setDetailCreative] = useState<Creative | null>(null);
  const [detailImageError, setDetailImageError] = useState(false);

  // Selection helper functions
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  const selectedCreatives = creatives.filter(c => selectedIds.has(c.id)).map(c => ({
    id: c.id,
    filename: c.filename,
    thumbnail_url: c.thumbnail_url,
    creative_type: c.creative_type,
    meta_account_id: c.meta_account_id,
    group_id: c.group_id
  }));

  // Auto-pair selected creatives using naming conventions
  const handleAutoGroup = async () => {
    if (selectedIds.size < 2 || !user) return;

    setIsAutoPairing(true);
    try {
      // Filter out already-grouped assets
      const ungrouped = selectedCreatives.filter(c => !c.group_id);
      const skippedCount = selectedCreatives.length - ungrouped.length;

      if (ungrouped.length < 2) {
        toast({
          title: 'Already grouped',
          description: `${skippedCount} asset(s) already grouped, nothing new to pair.`,
        });
        setIsAutoPairing(false);
        return;
      }

      // Fetch all naming conventions
      const { data: conventions, error: convError } = await supabase
        .from('naming_conventions')
        .select('id, label, square_suffix, vertical_suffix, landscape_suffix')
        .or(`is_system_default.eq.true,user_id.eq.${user.id}`);

      if (convError || !conventions || conventions.length === 0) {
        toast({
          title: 'No matching pairs found',
          description: 'No matching pairs found. Try uploading with a naming convention or pair manually by selecting exactly 2 assets.',
          variant: 'destructive',
        });
        setIsAutoPairing(false);
        return;
      }

      // Helper: compute group key by stripping a matched suffix
      const computeGroupKey = (filename: string, suffixes: string[]): { key: string; matchedSuffix: string | null } => {
        let name = filename.replace(/\.[^/.]+$/, '');
        let matchedSuffix: string | null = null;
        for (const suffix of suffixes) {
          if (!suffix) continue;
          const escaped = suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`([_ \\-]?)${escaped}(?=[_ .\\-]|$)`, 'i');
          const match = name.match(regex);
          if (match) {
            name = name.slice(0, match.index!) + name.slice(match.index! + match[0].length);
            matchedSuffix = suffix;
            break;
          }
        }
        name = name.replace(/^[_ \-]+|[_ \-]+$/g, '').replace(/[_ \-]{2,}/g, '_');
        return { key: name, matchedSuffix };
      };

      // Accumulate valid groups across ALL conventions
      const allValidGroups: Array<{ members: { id: string; filename: string; meta_account_id: string; matchedSuffix: string }[]; groupType: string; groupKey: string }> = [];
      const matchedAssetIds = new Set<string>();

      for (const conv of conventions) {
        const allSuffixes = [conv.square_suffix, conv.vertical_suffix, conv.landscape_suffix].filter(Boolean) as string[];
        if (allSuffixes.length < 2) continue;

        // Only try assets not yet matched
        const remaining = ungrouped.filter(a => !matchedAssetIds.has(a.id));
        if (remaining.length < 2) break;

        const groupMap = new Map<string, { id: string; filename: string; meta_account_id: string; matchedSuffix: string }[]>();

        for (const asset of remaining) {
          const { key, matchedSuffix } = computeGroupKey(asset.filename, allSuffixes);
          if (!matchedSuffix) continue;
          if (!groupMap.has(key)) groupMap.set(key, []);
          groupMap.get(key)!.push({ id: asset.id, filename: asset.filename, meta_account_id: asset.meta_account_id, matchedSuffix });
        }

        for (const [groupKey, members] of groupMap) {
          if (members.length < 2 || members.length > 3) continue;
          const uniqueSuffixes = new Set(members.map(m => m.matchedSuffix.toLowerCase()));
          if (uniqueSuffixes.size !== members.length) continue;
          const accounts = new Set(members.map(m => m.meta_account_id));
          if (accounts.size !== 1) continue;

          const groupType = members.length === 3 ? 'triple' : 'pair';
          allValidGroups.push({ members, groupType, groupKey });
          members.forEach(m => matchedAssetIds.add(m.id));
        }
      }

      if (allValidGroups.length === 0) {
        toast({
          title: 'No matching pairs found',
          description: 'No matching pairs found. Try uploading with a naming convention or pair manually by selecting exactly 2 assets.',
          variant: 'destructive',
        });
        setIsAutoPairing(false);
        return;
      }

      // Create groups for all collected valid entries
      let createdCount = 0;

      for (const { members, groupType, groupKey } of allValidGroups) {
        const { data: groupData, error: groupError } = await supabase
          .from('creative_groups')
          .insert({
            user_id: user.id,
            ad_account_id: members[0].meta_account_id,
            group_name: groupKey || 'Group',
            group_type: groupType,
          })
          .select()
          .single();

        if (groupError) {
          console.error('Failed to create group:', groupError);
          continue;
        }

        const { error: updateError } = await supabase
          .from('creative_metadata')
          .update({ group_id: groupData.id, group_type: groupType })
          .in('id', members.map(m => m.id));

        if (!updateError) {
          createdCount++;
        }
      }
      const unmatchedCount = ungrouped.length - matchedAssetIds.size;
      const parts: string[] = [];
      if (createdCount > 0) parts.push(`Created ${createdCount} pair${createdCount !== 1 ? 's' : ''}`);
      if (skippedCount > 0) parts.push(`${skippedCount} already grouped, skipped`);
      if (unmatchedCount > 0) parts.push(`${unmatchedCount} asset${unmatchedCount !== 1 ? 's' : ''} could not be matched`);

      toast({
        title: createdCount > 0 ? 'Pairing complete' : 'No pairs created',
        description: parts.join('. '),
        variant: createdCount > 0 ? 'default' : 'destructive',
      });

      clearSelection();
      fetchCreatives();
    } catch (err: unknown) {
      console.error('Auto-pairing error:', err);
      toast({ title: 'Error', description: parseErrorMessage(err), variant: 'destructive' });
    } finally {
      setIsAutoPairing(false);
    }
  };

  // Delete selected creatives
  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    
    setIsDeleting(true);
    let successCount = 0;
    let failCount = 0;
    const groupsToCheck = new Set<string>();

    try {
      // Get full creative data for selected items
      const creativesToDelete = creatives.filter(c => selectedIds.has(c.id));
      
      for (const creative of creativesToDelete) {
        try {
          // Track group_id for cleanup check
          if (creative.group_id) {
            groupsToCheck.add(creative.group_id);
          }

          // Delete thumbnail from storage if exists
          const tPath = (creative as any).thumbnail_path;
          if (tPath) {
            await supabase.storage.from('thumbnails').remove([tPath]);
          } else if (creative.thumbnail_url) {
            // Legacy fallback: extract path from URL
            const urlParts = creative.thumbnail_url.split('/thumbnails/');
            if (urlParts.length > 1) {
              const storagePath = urlParts[1].split('?')[0];
              await supabase.storage.from('thumbnails').remove([storagePath]);
            }
          }

          // Delete the creative_metadata record
          const { error: deleteError } = await supabase
            .from('creative_metadata')
            .delete()
            .eq('id', creative.id);

          if (deleteError) {
            throw deleteError;
          }

          successCount++;
        } catch (err) {
          console.error(`Failed to delete creative ${creative.id}:`, err);
          failCount++;
        }
      }

      // Clean up orphaned groups and update groups with only 1 remaining item
      for (const groupId of groupsToCheck) {
        // Count remaining items in this group
        const { data: remainingItems, error: countError } = await supabase
          .from('creative_metadata')
          .select('id')
          .eq('group_id', groupId);

        if (countError) {
          console.error(`Error checking group ${groupId}:`, countError);
          continue;
        }

        if (!remainingItems || remainingItems.length === 0) {
          // Delete orphaned group
          await supabase.from('creative_groups').delete().eq('id', groupId);
        } else if (remainingItems.length === 1) {
          // Update remaining item to 'single' type
          await supabase
            .from('creative_metadata')
            .update({ group_type: 'single', group_id: null })
            .eq('id', remainingItems[0].id);
          
          // Delete the now-empty group
          await supabase.from('creative_groups').delete().eq('id', groupId);
        }
      }

      // Show result toast
      if (failCount === 0) {
        toast({
          title: 'Success',
          description: `${successCount} creative${successCount !== 1 ? 's' : ''} deleted successfully`
        });
      } else if (successCount > 0) {
        toast({
          title: 'Partial Success',
          description: `Deleted ${successCount} of ${successCount + failCount} items. Some items failed to delete.`,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to delete selected items. Please try again.',
          variant: 'destructive'
        });
      }

      // Refresh and exit selection mode
      clearSelection();
      fetchCreatives();
    } catch (err) {
      console.error('Delete operation failed:', err);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };
  // Fetch creatives with server-side filters
  const fetchCreatives = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from('creative_metadata')
        .select('id, meta_creative_id, meta_account_id, creative_type, filename, dimensions, thumbnail_url, thumbnail_path, uploaded_at, aspect_ratio, group_type, group_id, tags')
        .eq('user_id', user.id)
        .or('thumbnail_url.neq.,thumbnail_path.neq.')
        .order('uploaded_at', { ascending: false });
      
      // Always filter by the selected ad account from context
      if (selectedAdAccountId) {
        query = query.eq('meta_account_id', selectedAdAccountId);
      }
      
      if (selectedType !== 'all') {
        query = query.eq('creative_type', selectedType);
      }
      
      if (searchTerm.trim()) {
        query = query.ilike('filename', `%${searchTerm.trim()}%`);
      }

      if (groupTypeFilter !== 'all') {
        if (groupTypeFilter === 'single') {
          query = query.or('group_type.is.null,group_type.eq.single');
        } else {
          query = query.eq('group_type', groupTypeFilter);
        }
      }
      
      const { data, error: fetchError } = await query;
      
      if (fetchError) {
        throw fetchError;
      }
      
      setCreatives((data || []).map(d => ({ ...d, tags: (d.tags as Record<string, string>) || null })));
    } catch (err) {
      console.error('Error fetching creatives:', err);
      setError('Failed to load creatives');
      toast({
        title: 'Error',
        description: 'Failed to load creatives. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [user, selectedAdAccountId, selectedType, searchTerm, groupTypeFilter, toast]);

  useEffect(() => {
    fetchCreatives();
  }, [fetchCreatives]);

  // Sort creatives so grouped assets appear adjacent (feed before vertical)
  const sortedCreatives = useMemo(() => {
    if (!creatives || creatives.length === 0) return creatives;

    const groupTimestamps: Record<string, number> = {};
    creatives.forEach(c => {
      if (c.group_id) {
        const t = new Date(c.uploaded_at || '').getTime();
        if (!groupTimestamps[c.group_id] || t > groupTimestamps[c.group_id]) {
          groupTimestamps[c.group_id] = t;
        }
      }
    });

    return [...creatives].sort((a, b) => {
      if (a.group_id && b.group_id && a.group_id === b.group_id) {
        // Feed (square ~1:1) first, then vertical
        const aRatio = calculateAspectRatioLabel(a.dimensions);
        const bRatio = calculateAspectRatioLabel(b.dimensions);
        const aIsFeed = aRatio === '1:1 (Square)' || aRatio === '4:5';
        const bIsFeed = bRatio === '1:1 (Square)' || bRatio === '4:5';
        if (aIsFeed && !bIsFeed) return -1;
        if (!aIsFeed && bIsFeed) return 1;
        return 0;
      }
      const aTime = a.group_id ? (groupTimestamps[a.group_id] || 0) : new Date(a.uploaded_at || '').getTime();
      const bTime = b.group_id ? (groupTimestamps[b.group_id] || 0) : new Date(b.uploaded_at || '').getTime();
      return bTime - aTime;
    });
  }, [creatives]);

  // Client-side filter for aspect ratio
  const filteredCreatives = useMemo(() => {
    if (selectedRatio === 'all') return sortedCreatives;
    
    return sortedCreatives.filter(creative => {
      const ratioLabel = calculateAspectRatioLabel(creative.dimensions);
      return ratioLabel === selectedRatio;
    });
  }, [sortedCreatives, selectedRatio]);

  // Generate signed URLs for all visible creatives
  const thumbnailPaths = useMemo(() => filteredCreatives.map(c => (c as any).thumbnail_path), [filteredCreatives]);
  const { urlMap: signedUrlMap } = useSignedThumbnailUrls(thumbnailPaths);

  const handleClearFilters = () => {
    setSelectedType('all');
    setSearchTerm('');
    setSelectedRatio('all');
    setGroupTypeFilter('all');
  };

  const handleCreativeClick = (creative: Creative) => {
    setDetailCreative(creative);
  };

  const handleTagsUpdated = useCallback((creativeId: string, tags: Record<string, string>) => {
    setCreatives(prev => prev.map(c => c.id === creativeId ? { ...c, tags } : c));
    if (detailCreative?.id === creativeId) {
      setDetailCreative(prev => prev ? { ...prev, tags } : prev);
    }
  }, [detailCreative?.id]);

  const hasActiveFilters = selectedType !== 'all' || searchTerm.trim() !== '' || selectedRatio !== 'all' || groupTypeFilter !== 'all';

  if (!selectedAdAccountId) {
    return (
      <div className="p-6 flex flex-col items-center justify-center py-16 text-center">
        <div className="rounded-full bg-muted p-4 mb-4">
          <ImageIcon className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No ad account selected</h3>
        <p className="text-sm text-muted-foreground">
          Please select an ad account from the sidebar to view your creatives.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Creative Library</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Browse and manage your uploaded creative assets
            </p>
          </div>
          <Button
            variant={selectionMode ? "secondary" : "outline"}
            onClick={() => {
              setSelectionMode(!selectionMode);
              if (selectionMode) clearSelection();
            }}
          >
            <CheckSquare className="h-4 w-4 mr-2" />
            {selectionMode ? 'Exit Select' : 'Select'}
          </Button>
        </div>

        {/* Filters */}
        <CreativeFilters
          selectedType={selectedType}
          onTypeChange={setSelectedType}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedRatio={selectedRatio}
          onRatioChange={setSelectedRatio}
          groupTypeFilter={groupTypeFilter}
          onGroupTypeFilterChange={setGroupTypeFilter}
        />

        {/* Results counter */}
        <div className="text-sm text-muted-foreground">
          {loading ? (
            <Skeleton className="h-4 w-32" />
          ) : (
            <>
              Showing {filteredCreatives.length} creative{filteredCreatives.length !== 1 ? 's' : ''}
              {hasActiveFilters && creatives.length !== filteredCreatives.length && (
                <span> of {creatives.length} total</span>
              )}
            </>
          )}
        </div>

        {/* Selection toolbar */}
        {selectionMode && (
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg border">
            <div className="flex items-center gap-3">
              <Badge variant="secondary">
                {selectedIds.size} selected
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const allIds = new Set(filteredCreatives.map(c => c.id));
                  setSelectedIds(allIds);
                }}
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                disabled={selectedIds.size === 0}
                onClick={() => setBulkTagEditorOpen(true)}
              >
                <Tag className="h-4 w-4 mr-2" />
                Edit Tags
              </Button>
              <Button
                variant="destructive"
                disabled={selectedIds.size === 0}
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedIds.size})
              </Button>
              <Button
                disabled={selectedIds.size < 2 || isAutoPairing}
                onClick={handleAutoGroup}
              >
                <Layers className="h-4 w-4 mr-2" />
                {isAutoPairing ? 'Pairing...' : `Group Selected (${selectedIds.size})`}
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-square w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-destructive/10 p-4 mb-4">
              <SearchX className="h-8 w-8 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Creatives</h3>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={fetchCreatives} variant="outline">
              Try Again
            </Button>
          </div>
        ) : filteredCreatives.length === 0 ? (
          hasActiveFilters ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <SearchX className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No creatives match your filters</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your filters or upload new creatives
              </p>
              <Button onClick={handleClearFilters} variant="outline">
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No creatives in your library yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Upload your first creative to get started
              </p>
              <Button onClick={() => navigate('/creatives/upload')}>
                <Upload className="mr-2 h-4 w-4" />
                Upload Creative
              </Button>
            </div>
          )
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredCreatives.map((creative) => (
              <CreativeCard
                key={creative.id}
                creative={creative}
                signedThumbnailUrl={resolveThumbnailUrl(signedUrlMap, (creative as any).thumbnail_path, creative.thumbnail_url)}
                onClick={() => selectionMode ? toggleSelection(creative.id) : handleCreativeClick(creative)}
                selectionMode={selectionMode}
                isSelected={selectedIds.has(creative.id)}
                onToggleSelect={() => toggleSelection(creative.id)}
                onRefresh={fetchCreatives}
              />
            ))}
          </div>
        )}

        {/* Bulk grouping dialog removed — auto-pairing is inline */}

        {/* Bulk tag editor */}
        <BulkTagEditor
          open={bulkTagEditorOpen}
          onOpenChange={setBulkTagEditorOpen}
          selectedIds={selectedIds}
          onSuccess={() => {
            fetchCreatives();
          }}
        />

        {/* Creative detail dialog with tag editor */}
        <Dialog open={!!detailCreative} onOpenChange={(open) => { if (!open) { setDetailCreative(null); setDetailImageError(false); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-sm font-medium truncate pr-6">{detailCreative?.filename}</DialogTitle>
            </DialogHeader>
            {detailCreative && (
              <div className="space-y-4">
                {/* Thumbnail */}
                {detailCreative.thumbnail_url && (
                  <div className="aspect-video bg-muted rounded-md overflow-hidden">
                    {!detailImageError ? (
                      <img
                        src={detailCreative.thumbnail_url}
                        alt={detailCreative.filename}
                        className="w-full h-full object-contain"
                        onError={() => setDetailImageError(true)}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
                        <ImageOff className="h-10 w-10" />
                        <span className="text-xs">Thumbnail expired</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div>Type: <span className="text-foreground capitalize">{detailCreative.creative_type}</span></div>
                  <div>Dimensions: <span className="text-foreground">{detailCreative.dimensions || 'Unknown'}</span></div>
                  <div>Ratio: <span className="text-foreground">{calculateAspectRatioLabel(detailCreative.dimensions)}</span></div>
                  {detailCreative.group_type && detailCreative.group_type !== 'single' && (
                    <div>Group: <span className="text-foreground capitalize">{detailCreative.group_type}</span></div>
                  )}
                </div>
                {/* Tag editor */}
                <CreativeTagEditor
                  creativeId={detailCreative.id}
                  initialTags={detailCreative.tags}
                  onTagsUpdated={(tags) => handleTagsUpdated(detailCreative.id, tags)}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete confirmation dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete {selectedIds.size} Creative{selectedIds.size !== 1 ? 's' : ''}?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>
                  This will permanently delete the selected assets from your library. 
                  This action cannot be undone.
                </p>
                <p className="text-destructive/80 text-sm">
                  Note: Assets already uploaded to Meta will remain in your Meta Ad Library.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteSelected}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </div>
  );
};

export default CreativeLibrary;
